import { useTranslation } from "react-i18next";
import {
    Box,
    Button,
    Checkbox,
    HStack,
    Input,
    VStack,
    Text,
    Table,
    Spinner,
    IconButton,
    Link,
    Select,
} from "@chakra-ui/react";
import { HiArrowRight, HiArrowUp, HiArrowDown, HiStar, HiPhone } from "react-icons/hi2";
import { Tooltip } from "@/components/ui/tooltip";
import InteractionStatusBadge from "./InteractionStatusBadge";
import ChannelBadge from "./ChannelBadge";
import MobileFiltersPanel from "./MobileFiltersPanel";
import dayjs from "dayjs";

const extractPhone = (conv) => {
    if (conv.whatsAppId) return conv.whatsAppId;
    if (!conv.collectedSlots) return null;
    try {
        const slots = typeof conv.collectedSlots === 'string' ? JSON.parse(conv.collectedSlots) : conv.collectedSlots;
        return slots.phoneNumber ?? slots.phone ?? null;
    } catch {}
    return null;
};

const extractFromEntities = (conv, name) => {
    if (!conv.collectedAnswers) return null;
    try {
        const entities = typeof conv.collectedAnswers === 'string'
            ? JSON.parse(conv.collectedAnswers)
            : conv.collectedAnswers;
        return entities.find(e => e.name === name)?.value ?? null;
    } catch {}
    return null;
};

const InteractionList = ({
    conversations,
    isLoading,
    page,
    pageSize,
    totalCount,
    onPageChange,
    onPageSizeChange,
    sortBy,
    sortDir,
    onSort,
    showFavoriteOnly,
    onFavoriteToggle,
    searchQuery,
    onSearchChange,
    statusFilter,
    onStatusChange,
    statusCollection,
    onRowClick,
    selectedIds,
    onToggleSelect,
    onToggleAll,
    convFavLoadingId,
    onToggleFavorite,
}) => {
    const { t } = useTranslation("models");

    const isAllSelected = conversations.length > 0 && selectedIds?.size === conversations.length;
    const isIndeterminate = selectedIds?.size > 0 && selectedIds?.size < conversations.length;
    const showCheckboxes = !!selectedIds;
    const totalPages = Math.ceil(totalCount / pageSize);

    return (
        <VStack w="full" align="flex-start" gap={3}>
            <MobileFiltersPanel
                searchQuery={searchQuery}
                onSearchChange={onSearchChange}
                statusFilter={statusFilter}
                onStatusChange={(val) => onStatusChange(val)}
                statusCollection={statusCollection}
                showFavoriteOnly={showFavoriteOnly}
                onFavoriteToggle={onFavoriteToggle}
            />
            <HStack w="full" justify="space-between" align="center">
                <HStack>
                    <Text fontWeight={700} fontSize="lg" color="gray.900">
                        {t("recruitmentDetailPage.conversationsListTitle")}
                    </Text>
                    <IconButton
                        display={{ base: 'none', md: 'flex' }}
                        variant="ghost"
                        size="sm"
                        aria-label="showFavoriteOnly"
                        color={showFavoriteOnly ? "yellow.400" : "gray.500"}
                        onClick={onFavoriteToggle}
                    >
                        <HiStar />
                    </IconButton>
                </HStack>
                <HStack gap={2} display={{ base: 'none', md: 'flex' }}>
                    <Input
                        placeholder={t("recruitmentDetailPage.searchPlaceholder")}
                        value={searchQuery}
                        onChange={(e) => onSearchChange(e.target.value)}
                        size="md"
                        w="200px"
                        bg="white"
                        border="1px solid"
                        borderColor="gray.200"
                        borderRadius="md"
                        color="gray.900"
                        _placeholder={{ color: "gray.400" }}
                    />
                    <Select.Root
                        collection={statusCollection}
                        value={[statusFilter]}
                        onValueChange={(e) => onStatusChange(e.value[0] ?? "")}
                        size="xs"
                        w={'180px'}
                    >
                        <Select.HiddenSelect />
                        <Select.Control>
                            <Select.Trigger
                                bg="white"
                                border="1px solid"
                                borderColor="gray.200"
                                borderRadius="md"
                                fontSize="sm"
                                color="gray.900"
                            >
                                <Select.ValueText color="gray.900" />
                            </Select.Trigger>
                            <Select.IndicatorGroup>
                                <Select.Indicator color="gray.500" />
                            </Select.IndicatorGroup>
                        </Select.Control>
                        <Select.Positioner>
                            <Select.Content bg="white" borderColor="gray.200" boxShadow="0 4px 24px rgba(23,21,31,0.12)">
                                {statusCollection.items.map((opt) => (
                                    <Select.Item item={opt} key={opt.value} color="gray.900" _hover={{ bg: "purple.50" }}>
                                        {opt.label}
                                        <Select.ItemIndicator />
                                    </Select.Item>
                                ))}
                            </Select.Content>
                        </Select.Positioner>
                    </Select.Root>
                </HStack>
            </HStack>

            <Box w="full" position="relative">
                {isLoading && (
                    <VStack
                        position="absolute"
                        inset={0}
                        zIndex={1}
                        align="center"
                        justify="center"
                        bg="rgba(255,255,255,0.6)"
                        borderRadius={12}
                        minH={100}
                    >
                        <Spinner color="purple.400" size="lg" />
                    </VStack>
                )}

                {/* Desktop table */}
                <Box
                    display={{ base: 'none', md: 'block' }}
                    w="full"
                    bg="white"
                    border="1px solid"
                    borderColor="gray.200"
                    borderRadius={12}
                    overflowX="auto"
                    opacity={isLoading ? 0.4 : 1}
                    transition="opacity 0.15s"
                >
                    <Table.Root minW="1100px">
                        <Table.Header>
                            <Table.Row bg="gray.50" borderBottom="1px solid" borderColor="gray.200">
                                {showCheckboxes && (
                                    <Table.ColumnHeader py={3} px={5} border="none" w="40px">
                                        <Checkbox.Root
                                            checked={isIndeterminate ? "indeterminate" : isAllSelected}
                                            onCheckedChange={onToggleAll}
                                            aria-label="Select all"
                                        >
                                            <Checkbox.HiddenInput />
                                            <Checkbox.Control>
                                                <Checkbox.Indicator />
                                            </Checkbox.Control>
                                        </Checkbox.Root>
                                    </Table.ColumnHeader>
                                )}
                                <Table.ColumnHeader py={3} px={2} border="none" w="40px" />
                                <Table.ColumnHeader
                                    color="gray.500" fontWeight={600} fontSize="xs"
                                    textTransform="uppercase" letterSpacing="wider"
                                    py={3} px={5} border="none"
                                >
                                    {t("recruitmentDetailPage.columnFullName")}
                                </Table.ColumnHeader>
                                <Table.ColumnHeader
                                    color="gray.500" fontWeight={600} fontSize="xs"
                                    textTransform="uppercase" letterSpacing="wider"
                                    py={3} px={5} border="none"
                                >
                                    {t("recruitmentDetailPage.columnStatus")}
                                </Table.ColumnHeader>
                                <Table.ColumnHeader
                                    color="gray.500" fontWeight={600} fontSize="xs"
                                    textTransform="uppercase" letterSpacing="wider"
                                    py={3} px={5} border="none"
                                    cursor="pointer"
                                    onClick={() => onSort("createdat")}
                                    _hover={{ color: "gray.900" }}
                                >
                                    <HStack gap={1}>
                                        <Text>{t("recruitmentDetailPage.columnStartedAt")}</Text>
                                        {sortBy === "createdat" && (sortDir === "asc" ? <HiArrowUp size={12} /> : <HiArrowDown size={12} />)}
                                    </HStack>
                                </Table.ColumnHeader>
                                <Table.ColumnHeader
                                    color="gray.500" fontWeight={600} fontSize="xs"
                                    textTransform="uppercase" letterSpacing="wider"
                                    py={3} px={5} border="none"
                                    cursor="pointer"
                                    onClick={() => onSort("updatedat")}
                                    _hover={{ color: "gray.900" }}
                                >
                                    <HStack gap={1}>
                                        <Text>{t("recruitmentDetailPage.columnLastMessageAt")}</Text>
                                        {sortBy === "updatedat" && (sortDir === "asc" ? <HiArrowUp size={12} /> : <HiArrowDown size={12} />)}
                                    </HStack>
                                </Table.ColumnHeader>
                                <Table.ColumnHeader
                                    color="gray.500" fontWeight={600} fontSize="xs"
                                    textTransform="uppercase" letterSpacing="wider"
                                    py={3} px={5} border="none"
                                >
                                    {t("recruitmentDetailPage.columnPhoneNumber")}
                                </Table.ColumnHeader>
                                <Table.ColumnHeader
                                    color="gray.500" fontWeight={600} fontSize="xs"
                                    textTransform="uppercase" letterSpacing="wider"
                                    py={3} px={5} border="none"
                                >
                                    {t("recruitmentDetailPage.columnEmail")}
                                </Table.ColumnHeader>
                                <Table.ColumnHeader
                                    color="gray.500" fontWeight={600} fontSize="xs"
                                    textTransform="uppercase" letterSpacing="wider"
                                    py={3} px={5} border="none"
                                >
                                    {t("recruitmentDetailPage.columnChannel")}
                                </Table.ColumnHeader>
                                <Table.ColumnHeader
                                    color="gray.500" fontWeight={600} fontSize="xs"
                                    textTransform="uppercase" letterSpacing="wider"
                                    py={3} px={5} border="none" textAlign="right"
                                >
                                    {t("recruitmentDetailPage.columnActions")}
                                </Table.ColumnHeader>
                            </Table.Row>
                        </Table.Header>
                        <Table.Body>
                            {conversations.map((conv) => (
                                <Tooltip
                                    key={conv.id}
                                    content={extractFromEntities(conv, "@document_summary")}
                                    disabled={!extractFromEntities(conv, "@document_summary")}
                                    openDelay={400}
                                    showArrow
                                >
                                    <Table.Row
                                        bg="white"
                                        borderBottom="1px solid"
                                        borderColor="gray.200"
                                        _hover={{ bg: "gray.50" }}
                                        transition="background 0.15s"
                                        onClick={() => onRowClick(conv)}
                                        cursor="pointer"
                                    >
                                        {showCheckboxes && (
                                            <Table.Cell py={4} px={5} border="none" w="40px" onClick={(e) => e.stopPropagation()}>
                                                <Checkbox.Root
                                                    checked={selectedIds.has(conv.id)}
                                                    onCheckedChange={() => onToggleSelect(conv.id)}
                                                    aria-label="Select conversation"
                                                >
                                                    <Checkbox.HiddenInput />
                                                    <Checkbox.Control>
                                                        <Checkbox.Indicator />
                                                    </Checkbox.Control>
                                                </Checkbox.Root>
                                            </Table.Cell>
                                        )}
                                        <Table.Cell py={4} px={2} border="none" w="40px" onClick={(e) => e.stopPropagation()}>
                                            {onToggleFavorite && (
                                                <IconButton
                                                    variant="ghost"
                                                    size="sm"
                                                    aria-label="Ulubione"
                                                    color={conv.isFavorite ? "yellow.400" : "gray.500"}
                                                    loading={convFavLoadingId === conv.id}
                                                    onClick={(e) => onToggleFavorite(e, conv.id)}
                                                >
                                                    <HiStar />
                                                </IconButton>
                                            )}
                                        </Table.Cell>
                                        <Table.Cell py={4} px={5} border="none">
                                            <Text color="gray.900" fontSize="sm">
                                                {extractFromEntities(conv, "@fullName") ?? "-"}
                                            </Text>
                                        </Table.Cell>
                                        <Table.Cell py={4} px={5} border="none">
                                            <InteractionStatusBadge status={conv.status} />
                                        </Table.Cell>
                                        <Table.Cell py={4} px={5} border="none">
                                            <Text color="gray.900" fontSize="sm">
                                                {dayjs(conv.createdAt).format('DD.MM.YYYY HH:mm')}
                                            </Text>
                                        </Table.Cell>
                                        <Table.Cell py={4} px={5} border="none">
                                            <Text color="gray.900" fontSize="sm">
                                                {dayjs(conv.updatedAt).format('DD.MM.YYYY HH:mm')}
                                            </Text>
                                        </Table.Cell>
                                        <Table.Cell py={4} px={5} border="none" onClick={(e) => e.stopPropagation()}>
                                            {extractPhone(conv)
                                                ? <Link href={`tel:${extractPhone(conv)}`} color="gray.900" fontSize="sm">{extractPhone(conv)}</Link>
                                                : <Text color="gray.900" fontSize="sm">-</Text>}
                                        </Table.Cell>
                                        <Table.Cell py={4} px={5} border="none">
                                            <Text color="gray.900" fontSize="sm">
                                                {extractFromEntities(conv, "@email") ?? "-"}
                                            </Text>
                                        </Table.Cell>
                                        <Table.Cell py={4} px={5} border="none">
                                            <ChannelBadge channel={conv.whatsAppId !== null ? "whatsapp" : "chat"} />
                                        </Table.Cell>
                                        <Table.Cell py={4} px={5} border="none" textAlign="right">
                                            <IconButton
                                                variant="outlined"
                                                size="sm"
                                                aria-label={t("recruitmentDetailPage.conversationDetails")}
                                                onClick={() => onRowClick(conv)}
                                            >
                                                <HiArrowRight />
                                            </IconButton>
                                        </Table.Cell>
                                    </Table.Row>
                                </Tooltip>
                            ))}
                        </Table.Body>
                    </Table.Root>
                </Box>

                {/* Mobile card list */}
                <VStack
                    display={{ base: 'flex', md: 'none' }}
                    w="full"
                    gap={2}
                    opacity={isLoading ? 0.4 : 1}
                    transition="opacity 0.15s"
                >
                    {conversations.map((conv) => (
                        <Tooltip
                            key={conv.id}
                            content={extractFromEntities(conv, "@document_summary")}
                            disabled={!extractFromEntities(conv, "@document_summary")}
                            openDelay={400}
                            showArrow
                        >
                            <Box
                                w="full"
                                bg="white"
                                border="1px solid"
                                borderColor="gray.200"
                                borderRadius={10}
                                p={3}
                                cursor="pointer"
                                onClick={() => onRowClick(conv)}
                                _active={{ opacity: 0.8 }}
                            >
                                <HStack justify="space-between" align="flex-start">
                                    <HStack gap={2} flex={1} minW={0}>
                                        {onToggleFavorite && (
                                            <IconButton
                                                variant="ghost"
                                                size="sm"
                                                aria-label="Ulubione"
                                                color={conv.isFavorite ? "yellow.400" : "gray.500"}
                                                loading={convFavLoadingId === conv.id}
                                                flexShrink={0}
                                                onClick={(e) => onToggleFavorite(e, conv.id)}
                                            >
                                                <HiStar />
                                            </IconButton>
                                        )}
                                        <VStack align="flex-start" gap={1} flex={1} minW={0}>
                                            <HStack gap={2} flexWrap="wrap">
                                                <ChannelBadge channel={conv.whatsAppId !== null ? "whatsapp" : "chat"} />
                                                {extractPhone(conv) && (
                                                    <HStack gap={1}>
                                                        <HiPhone size={12} color="var(--chakra-colors-gray-500)" />
                                                        <Link href={`tel:${extractPhone(conv)}`} color="gray.500" fontSize="xs" onClick={(e) => e.stopPropagation()}>
                                                            {extractPhone(conv)}
                                                        </Link>
                                                    </HStack>
                                                )}
                                            </HStack>
                                            {extractFromEntities(conv, "@fullName") && (
                                                <Text color="gray.500" fontSize="xs">
                                                    {extractFromEntities(conv, "@fullName")}
                                                </Text>
                                            )}
                                            {extractFromEntities(conv, "@email") && (
                                                <Text color="gray.500" fontSize="xs">
                                                    {extractFromEntities(conv, "@email")}
                                                </Text>
                                            )}
                                            <Text color="gray.500" fontSize="xs" whiteSpace="nowrap">
                                                {t("recruitmentDetailPage.columnStartedAt")}: {dayjs(conv.createdAt).format('DD.MM.YYYY HH:mm')}
                                            </Text>
                                        </VStack>
                                    </HStack>
                                    <HStack gap={1} flexShrink={0}>
                                        <InteractionStatusBadge status={conv.status} />
                                    </HStack>
                                </HStack>
                            </Box>
                        </Tooltip>
                    ))}
                </VStack>
            </Box>

            {totalCount > 0 && (
                <VStack w="full" gap={2} pt={1}>
                    <HStack justify="space-between" w="full">
                        <HStack gap={2}>
                            <Text color="gray.500" fontSize="sm" display={{ base: 'none', md: 'block' }}>
                                {t("recruitmentDetailPage.paginationRowsPerPage")}
                            </Text>
                            <select
                                value={pageSize}
                                onChange={(e) => onPageSizeChange(Number(e.target.value))}
                                style={{
                                    background: "white",
                                    border: "1px solid var(--chakra-colors-gray-200)",
                                    borderRadius: "6px",
                                    color: "var(--chakra-colors-gray-900)",
                                    fontSize: "var(--chakra-fontSizes-sm)",
                                    padding: "3px 8px",
                                    cursor: "pointer",
                                }}
                            >
                                <option value={20}>20</option>
                                <option value={50}>50</option>
                                <option value={100}>100</option>
                            </select>
                            <Text color="gray.500" fontSize="sm">
                                {t("recruitmentDetailPage.paginationInfo", {
                                    page,
                                    totalPages,
                                    totalCount,
                                })}
                            </Text>
                        </HStack>
                        {totalPages > 1 && (
                            <HStack gap={2}>
                                <Button
                                    variant="outlined"
                                    size="sm"
                                    onClick={() => onPageChange(page - 1)}
                                    disabled={page <= 1}
                                >
                                    {t("recruitmentDetailPage.paginationPrev")}
                                </Button>
                                <Button
                                    variant="outlined"
                                    size="sm"
                                    onClick={() => onPageChange(page + 1)}
                                    disabled={page >= totalPages}
                                >
                                    {t("recruitmentDetailPage.paginationNext")}
                                </Button>
                            </HStack>
                        )}
                    </HStack>
                </VStack>
            )}
        </VStack>
    );
};

export default InteractionList;
